import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, MapPin, Phone, Users } from 'lucide-react';
import { Card, Badge, Button, Alert, Spinner, BloodTypeBadge, MapPreview, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';
import { formatVenue } from '../utils/maps';

export default function OrgCampDetail() {
  const { id } = useParams();
  const isMobile = useIsMobile();
  const [camp, setCamp] = useState(null);
  const [regs, setRegs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [marking, setMarking] = useState(null);
  const [markError, setMarkError] = useState('');
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    setLoading(true);
    // The registrations endpoint is scoped to the caller's own camps, so it —
    // not the public camp lookup — is what decides whether this page is
    // viewable at all.
    Promise.all([
      api.get(`/organizations/camps/${id}`),
      api.get(`/organizations/camps/${id}/registrations`),
    ])
      .then(([c, r]) => { setCamp(c.data); setRegs(r.data); })
      .catch((e) => setError(
        e.response?.status === 404
          ? "That camp doesn't exist, or it isn't one of yours."
          : "Couldn't load this camp right now. Please try again."
      ))
      .finally(() => setLoading(false));
  }, [id]);

  const markDonated = async (reg) => {
    setMarking(reg.id);
    setMarkError('');
    try {
      const { data } = await api.post(`/organizations/camps/${id}/registrations/${reg.id}/donated`);
      // The donor is now in cooldown, so the row's eligibility flips too.
      setRegs(rs => rs.map(r => (r.id === reg.id
        ? { ...r, attended: true, is_eligible: false, total_donations: r.total_donations + 1 }
        : r)));
      setOutcome(data);
      setConfirmId(null);
    } catch (e) {
      setMarkError(e.response?.data?.detail || 'Could not record that donation. Please try again.');
    } finally {
      setMarking(null);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Alert variant="error">{error}</Alert>
        <Link to="/org/dashboard"><Button variant="secondary" size="sm">Back to dashboard</Button></Link>
      </PageLayout>
    );
  }

  const date = new Date(camp.camp_date);
  // Cancelled slots are shown separately: they're history, not attendees, and
  // they don't count toward the camp's numbers.
  const active = regs.filter(r => !r.cancelled_at);
  const cancelled = regs.filter(r => r.cancelled_at);
  const eligibleCount = active.filter(r => r.is_eligible).length;
  const donatedCount = active.filter(r => r.attended).length;
  // Date-level, matching the server guard: donations can be recorded from the
  // camp's own day onwards, never before it.
  const campStarted = new Date(new Date().toDateString()) >= new Date(date.toDateString());

  return (
    <PageLayout>
      <div style={{ marginBottom: '16px' }}>
        <Link to="/org/dashboard" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gray-500)' }}>← Back to dashboard</Link>
        <h1 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)', marginTop: '8px' }}>{camp.title}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
          <Badge color="blue">{camp.status}</Badge>
          <span style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
            {camp.slots_booked} of {camp.slots_total} slots filled
          </span>
        </div>
      </div>

      {/* Camp details */}
      <Card style={{ marginBottom: '16px' }}>
        {camp.description && (
          <p style={{ fontSize: '13.5px', color: 'var(--gray-600)', lineHeight: 1.6, marginBottom: '14px' }}>{camp.description}</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', fontSize: '13px', color: 'var(--gray-600)', marginBottom: '16px' }}>
          <span style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
            <MapPin size={14} color="var(--gray-400)" style={{ flexShrink: 0, marginTop: '2px' }} />
            {formatVenue(camp)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <Calendar size={14} color="var(--gray-400)" />
            {date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            {' · '}
            {date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            {camp.end_time && ` – ${new Date(camp.end_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
          </span>
          {(camp.contact_name || camp.contact_phone) && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Phone size={14} color="var(--gray-400)" />
              {[camp.contact_name, camp.contact_phone].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>

        {camp.latitude != null ? (
          <MapPreview place={camp} label="Open in Maps" />
        ) : (
          <Alert variant="warning" style={{ marginBottom: 0 }}>
            This camp has no map location, so donors can't get directions to it. Creating it again with a
            more specific address — or pinning it on site — will place it on the map.
          </Alert>
        )}
      </Card>

      {/* Registered donors */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>
            <Users size={15} color="var(--gray-400)" /> Registered Donors
          </span>
          <span style={{ fontSize: '12.5px', color: 'var(--gray-500)' }}>
            {active.length} registered{active.length > 0 && ` · ${donatedCount} donated · ${eligibleCount} eligible`}
          </span>
        </div>

        {outcome && (
          <Alert variant="success">
            Recorded {outcome.donor_name}'s donation — certificate {outcome.certificate_number} issued.
            They can donate again from {new Date(outcome.cooldown_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.
            {outcome.cancelled_registrations.length > 0 && (
              <>
                {' '}Their slot was released at{' '}
                {outcome.cancelled_registrations
                  .map(c => `${c.title} (${new Date(c.camp_date).toLocaleDateString()})`)
                  .join(', ')}
                {' '}— those camps fall inside their recovery period.
              </>
            )}
          </Alert>
        )}
        {markError && <Alert variant="error">{markError}</Alert>}

        {!campStarted && active.length > 0 && (
          <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: '12px' }}>
            You'll be able to record donations from {date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, the day of the camp.
          </p>
        )}

        {active.length === 0 ? (
          <p style={{ fontSize: '13.5px', color: 'var(--gray-400)', padding: '12px 0' }}>
            No donors have registered for this camp yet.
          </p>
        ) : active.map((r, i) => (
          <div
            key={r.id}
            style={{
              display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px',
              padding: '13px 0',
              borderBottom: i < active.length - 1 ? '1px solid var(--gray-100)' : 'none',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <BloodTypeBadge type={r.blood_type} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)' }}>
                {r.name}
                {!r.is_eligible && !r.attended && <span style={{ marginLeft: '8px' }}><Badge color="yellow">not eligible</Badge></span>}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '3px', wordBreak: 'break-word' }}>
                <a href={`tel:${r.phone}`} style={{ color: 'var(--gray-600)', fontWeight: 500 }}>{r.phone}</a>
                {' · '}
                <a href={`mailto:${r.email}`} style={{ color: 'var(--gray-600)' }}>{r.email}</a>
                {r.city && ` · ${r.city}`}
              </div>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--gray-400)', textAlign: isMobile ? 'left' : 'right', flexShrink: 0 }}>
              <div>{r.total_donations} donation{r.total_donations !== 1 ? 's' : ''}</div>
              <div>registered {new Date(r.registered_at).toLocaleDateString()}</div>
            </div>

            {/* Recording a donation is one-way and starts a 56-day cooldown, so
                it takes a deliberate second tap rather than a single click. */}
            <div style={{ flexShrink: 0 }}>
              {r.attended ? (
                <Badge color="green">✓ Donated</Badge>
              ) : !campStarted ? null : confirmId === r.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11.5px', color: 'var(--gray-500)', maxWidth: '210px' }}>
                    Confirms {r.name} donated today. Locks them for 56 days and releases any camp slots inside that window.
                  </span>
                  <Button size="sm" variant="primary" loading={marking === r.id} onClick={() => markDonated(r)}>
                    Yes, donated
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>Cancel</Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setMarkError(''); setOutcome(null); setConfirmId(r.id); }}
                >
                  Mark donated
                </Button>
              )}
            </div>
          </div>
        ))}

        {cancelled.length > 0 && (
          <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid var(--gray-200)' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '8px' }}>
              Released slots ({cancelled.length})
            </div>
            {cancelled.map(r => (
              <div key={r.id} style={{ padding: '8px 0', fontSize: '12.5px', color: 'var(--gray-500)' }}>
                <span style={{ fontWeight: 600, color: 'var(--gray-600)' }}>{r.name}</span>
                {' · '}{r.blood_type}
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '2px' }}>
                  {r.cancellation_reason || 'Registration cancelled'}
                  {' · '}{new Date(r.cancelled_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PageLayout>
  );
}
